import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoChevronUp, IoChevronDown } from 'react-icons/io5';
import { useDebounce } from 'react-use';
import { ALLOWED_UNITS } from '@/features/editor/widgets/ai-component/inputs/shared/css-declarations';

export interface NumberUnitProps {
  initialValue?: string;
  onChange?: (value: string) => void;
  onError?: (error: string) => void;
  step?: number;
  noUnit?: boolean;
}

function includesCssTextUnit(
  value:
    | {
        number: number;
        unit: string;
        input?: string;
        raw?: string;
      }
    | undefined,
) {
  return [`none`, `auto`, `inherit`, `initial`, `unset`].includes(value?.raw);
}

export function NumberUnitInput({
  initialValue,
  onChange,
  onError,
  step = 1,
  noUnit = false,
}: NumberUnitProps) {
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const changeRef = useRef(0);
  const [error, setError] = useState(``);
  const [value, setValue] = useState(parse(initialValue));

  const formatted = useMemo(() => {
    if (value?.raw && includesCssTextUnit(value)) {
      return value.raw;
    }
    if (value.input) {
      return value.input;
    }
    return `${parseFloat(value.number.toFixed(3))}${value.unit}`;
  }, [value]);

  const inputWidth = useMemo(
    () => `${formatted.replace(`.`, ``).length + 1}ch`,
    [formatted],
  );

  useEffect(() => {
    if (onError) {
      onError(error);
    }
  }, [error]);

  useDebounce(
    () => {
      if (includesCssTextUnit(value)) {
        return;
      }
      if (!noUnit && value.input) {
        setError(`Use a valid format, e.g., "12px" or "52%"`);
        return;
      }
      if (Number.isNaN(value.number)) {
        setError(`Use a valid number, e.g., "12px" or "52%"`);
        return;
      }
      if (!noUnit && !!value.number && !ALLOWED_UNITS.includes(value.unit)) {
        setError(`Use a valid css unit, e.g., "12px" or "52%"`);
        return;
      }
      setError(``);

      if (onChange && changeRef.current > 0) {
        onChange(formatted);
      }
      changeRef.current += 1;
    },
    500,
    [value],
  );

  const onInputChanged = (ev) => {
    const stringValue = ev.target.value;
    const { number: numVal, unit: unitVal, input, raw } = parse(stringValue);
    setValue({
      number: numVal,
      unit: unitVal || ``,
      input,
      raw,
    });
  };

  const startChanging = (changeFn: () => void) => {
    changeFn();
    intervalRef.current = setInterval(changeFn, 200);
  };

  const stopChanging = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  function increase(multiplier = 1) {
    setValue((prev) => ({
      number: prev.number + step * multiplier,
      unit: prev.unit,
    }));
  }

  function decrease(multiplier = 1) {
    setValue((prev) => ({
      number: prev.number - step * multiplier,
      unit: prev.unit,
    }));
  }

  function onKeyDown(e) {
    const multiplier = e.shiftKey ? 10 : 1;

    if (e.key === `ArrowUp`) {
      e.preventDefault();
      increase(multiplier);
    } else if (e.key === `ArrowDown`) {
      e.preventDefault();
      decrease(multiplier);
    }
  }

  return (
    <TopWrapper>
      <InputWrapper>
        <input
          type={noUnit ? `number` : undefined}
          style={{ width: inputWidth }}
          value={formatted}
          onChange={onInputChanged}
          onKeyDown={onKeyDown}
          onKeyUp={stopChanging}
        />
        <ControlsWrapper>
          <IoChevronUp
            size={14}
            onMouseDown={(e) => {
              const multiplier = e.shiftKey ? 10 : 1;
              startChanging(() => increase(multiplier));
            }}
            onMouseUp={stopChanging}
          />
          <IoChevronDown
            size={14}
            onMouseDown={(e) => {
              const multiplier = e.shiftKey ? 10 : 1;
              startChanging(() => decrease(multiplier));
            }}
            onMouseUp={stopChanging}
          />
        </ControlsWrapper>
      </InputWrapper>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </TopWrapper>
  );
}

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 0;

  input {
    appearance: none;
    outline: none;
    border: none;
    padding: 0;
    margin: 0;

    background-color: transparent;
    font-family: Inter, sans-serif;
    color: rgb(75, 85, 100);
    font-size: 1.4rem;
    font-weight: 500;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  color: rgb(75, 85, 100);

  & > * {
    cursor: pointer;
    user-select: none;

    &:hover {
      color: rgb(59, 130, 246);
    }
  }
`;

const ErrorMessage = styled.div`
  font-family: Inter, sans-serif;
  color: rgb(213, 38, 38);
  opacity: 0.7;
  text-align: end;
  font-size: 1.2rem;
  font-weight: 500;
`;

const parse = (
  input: string,
): { number: number; unit: string; input?: string; raw?: string } => {
  const match = input?.match?.(/^(-?\d*\.?\d+)([a-z%]*)$/i);
  if (!match) return { number: 0, unit: ``, input, raw: input };
  return {
    number: parseFloat(match[1]),
    unit: match[2] || ``,
    input: undefined,
    raw: input,
  };
};
